import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Stethoscope, Users, Award } from 'lucide-react';

const AboutUs = ({ isAdmin = false, onEditClick }) => {
  const [aboutData, setAboutData] = useState({
    title: "",
    description: "",
    features: [],
    imageUrl: "",
    buttonText: "",
    buttonLink:"",
  });
  const [isLoading, setIsLoading] = useState(true);

  const colors = {
    primaryBlue: '#0760ad',
    primaryGreen: '#098a48',
    teal: '#118a94',
    secondaryGreen: '#339166',
    white: '#FFFFFF',
    lightGray: '#f8fafc',
    darkText: '#1a365d',
    lightText: '#4a5568'
  };

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/home/about-us/`);
        if (response.ok) {
          const data = await response.json();
          setAboutData({
            title: data.title || "About Warm Wellness Center",
            description: data.description || "Our journey began with a simple yet profound mission...",
            features: data.features || [],
            imageUrl: data.imageUrl || "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            buttonText: data.buttonText || "Learn More About Us",
            buttonLink:data.buttonLink || "",
          });
        }
      } catch (error) {
        console.error('Error fetching about us data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (isLoading) {
    return (
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 flex ">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </section>
    );
  }

  const featureIcons = [<Building2 className="w-6 h-6" />, <Stethoscope className="w-6 h-6" />, <Users className="w-6 h-6" />, <Award className="w-6 h-6" />];

  return (
    <section className="py-12 bg-white relative  text-justify">
      {isAdmin && (
        <button 
          onClick={onEditClick}
          className="absolute top-4 right-4 btn btn-sm btn-primary z-10"
        >
          Edit Section
        </button>
      )}
      
      <div className="container mx-auto px-4">
        <div className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-sm">
          <div className="flex flex-col md:flex-row gap-12">
            {/* Text Content */}
            <div className="md:w-1/2">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: colors.primaryBlue }}>
                  {aboutData.title}
                </h2>
                
                <div className="w-16 h-1 mb-6" style={{ background: `linear-gradient(90deg, ${colors.primaryBlue}, ${colors.teal})` }}></div>
                
                <p className="text-lg mb-4" style={{ color: colors.darkText }}>
                  {aboutData.description.split('\n')[0]}
                </p>
                
                <p className="mb-8" style={{ color: colors.lightText }}>
                  {aboutData.description.split('\n').slice(1).join('\n')}
                </p>
                
                {/* Features Grid */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {aboutData.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white">
                      <div className="p-2 rounded-full" style={{ backgroundColor: `${colors.primaryBlue}10`, color: colors.primaryBlue }}>
                        {featureIcons[index] || featureIcons[0]}
                      </div>
                      <span className="font-medium" style={{ color: colors.darkText }}>{feature.text}</span>
                    </div>
                  ))}
                </div>
                <motion.button
  whileHover={{ y: -2 }}
  whileTap={{ scale: 0.98 }}
  className="px-8 py-3 rounded-lg font-medium text-white"
  style={{ 
    background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.teal})`,
    boxShadow: `0 4px 14px ${colors.primaryBlue}30`
  }}
  onClick={() => window.location.href = aboutData.buttonLink} // Redirect on click
>
  {aboutData.buttonText}
</motion.button>

              </motion.div>
            </div>
            
            {/* Medical Center Image */}
            <div className="md:w-1/2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative h-full  rounded-xl overflow-hidden shadow-md"
              >
                <img 
                  src={aboutData.imageUrl} 
                  alt="Warm Wellness Center Medical Center" 
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                  }}
                />
                
                {/* Decorative Element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full" style={{ 
                  background: `linear-gradient(135deg, ${colors.primaryGreen}, ${colors.secondaryGreen})`,
                  opacity: 0.1
                }}></div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;